import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { ConnectContext } from '../context/connectContext';
import { Text, Div, Button, Row, Col, Radiobox, Dropdown, Anchor, Icon, Image} from 'atomize';
import { Bar, Doughnut } from 'react-chartjs-2';

import SimpleMap from '../components/GoogleMap/SimpleMap';

const PartnerPage = () => {

    // fetching the partner information using url param
    let { name } = useParams();
    const { resetContext, fetchPartner, partner, fetchELOsByPartner, elosByPartner, fetchFilesByPartner, loadingPartner, loadingELO, loadingPictures, checkIndex, contactUrl, filteredUrls, setCheckIndex } = useContext(ConnectContext);
    let filteredELOs = elosByPartner;
    const images = filteredUrls?.length > 0 ? filteredUrls : [];
    const currImage = images?.length > 0 ? images[checkIndex] : '';

    // image loading states
    const [carouselImageLoaded, setCarouselImageLoaded] = useState(false);
    const [contactImageLoaded, setContactImageLoaded] = useState(false);

    // reset loading state when image changes
    useEffect(() => {
        setCarouselImageLoaded(false);
    }, [currImage]);

    // interest exploration fields
    const interestFields = ["Career Talk", "Field Site Visit", "Curiosity Lab", "Networking"];
    const getInterestExploration = (partner, fields) => {
        return fields.filter(field => partner[field] === "TRUE")
    };
    const interestExploration = getInterestExploration(partner, interestFields);

    // pursuit partnership fields
    const partnerFields = ["Micro Pursuit", "Mini Pursuit", "Personal Pursuit", "Partner Pursuit"];
    const getPursuitPartnership = (partner, fields) => {
    return fields
        .filter(field => partner[field] === "TRUE")
    };
    const pursuitPartnership = getPursuitPartnership(partner, partnerFields);

    // wbl fields
    const wblFields = ["Job Shadow", "Internship", "Externship", "Employment"];
    const getWBL = (partner, fields) => {
    return fields
        .filter(field => partner[field] === "TRUE")
    };
    const wbl = getWBL(partner, wblFields);

    // community embedded fields
    const communityFields = ["Vision Coach", "Service Learning", "Civic Engagement", "Community Group/Class"];
    const getCommunity = (partner, fields) => {
    return fields
        .filter(field => partner[field] === "TRUE")
    };
    const community = getCommunity(partner, communityFields);

    const getHref = "https://" + partner["Website"];
    
    // career Cluster fields
    const careerClusterFields = [
        "Financial Services",
        "Management & Entrepeneurship",
        "Digital Technology",
        "Marketing & Sales",
        "Education",
        "Healthcare & Human Services",
        "Public Service & Safety",
        "Agriculture",
        "Energy & Natural Resources",
        "Construction",
        "Advanced Manufacturing",
        "Supply Chain & Transportation",
        "Hospitality, Events, & Tourism",
        "Arts, Entertainment, & Design"
    ];
    const getCareerClusters = (partner, fields) => {
        return fields.filter(field => partner[field] === "TRUE");
    };
  
    // specific career fields
    const getCareerArrayName = (Cluster) => {
        return Cluster + " Careers"
    }
    const getCareerArray = (Cluster) => {
        return partner[getCareerArrayName(Cluster)]?.length > 0 ? partner[getCareerArrayName(Cluster)].split(",") : [];
    }

    // year dropdown state management
    const [showTermDropdown, setShowTermDropdown] = useState(false);
    const [showYearDropdown, setShowYearDropdown] = useState(false);
    const [selectedTerm, setSelectedTerm] = useState("Cumulative");
    const [selectedYear, setSelectedYear] = useState("All");

    // term dropdown list
    const termList = (
        <Div
            p={{ x: "2rem", y: "0.5rem" }}
            bg="white"
            border="1px solid"
            borderColor="#be9c63"
        >
            {["Cumulative", "T1", "T2", "T3", "Summer"].map((term) => (
            <Anchor key={term} textColor="black" hoverTextColor="#ee7c3e" d="block" p={{ y: "0.25rem" }} onClick={() => setSelectedTerm(term)}>
                {term}
            </Anchor>
            ))}
        </Div>
    );

    // generate school years
    const generateSchoolYears = (startYear, numberOfYears) => {
        const years = [];
        for (let i = 0; i < numberOfYears; i++) {
            const year1 = startYear + i;
            const year2 = year1 + 1;
            years.unshift(`${year1} - ${year2}`); // Use unshift to add years to the start of the array
        }
        return years;
    };
    
    // filter out future school years
    const filterFutureYears = (years) => {
        const currentYear = new Date().getFullYear();
        return years.filter(year => {
        const [startYear, endYear] = year.split(' - ').map(Number);
        return startYear <= currentYear && endYear <= currentYear + 1;
        });
    };
    const allYears = generateSchoolYears(2023, 30);
    let validYears = filterFutureYears(allYears);
    validYears = ["All", ...validYears];

    // year dropdown list
    const sortList = (
        <Div
            p={{ x: "2rem", y: "0.5rem" }}
            bg="white"
            borderColor="#be9c63"
            border="1px solid"
        >
            {validYears.map((year) => (
            <Anchor key={year} textColor="black" hoverTextColor="#ee7c3e" d="block" p={{ y: "0.25rem" }} onClick={() => setSelectedYear(year)}>
                {year}
            </Anchor>
            ))}
        </Div>
    );

    // filtering on term/year
    if (selectedTerm !== "Cumulative") {
        filteredELOs = filteredELOs.filter((elo) => elo["ELO Term"] === selectedTerm);
    }
    if (selectedYear !== "All") {
        filteredELOs = filteredELOs.filter((elo) => elo["ELO Year"] === selectedYear);
    }

    // sum learners served
    const sumLearnersServed = (eloArray) => {
        return eloArray.reduce((sum, elo) => {
            return sum + Number(elo["Learners Served"] || 0);
        }, 0);
    }
      
    // colors for both graphs
    const colors = [
        "#5b9dd0", // Light Blue
        "#53813e", // Green
        "#ee7c3e", // Orange
        "#654b1e", // Brown
        "#4C82AC", // Dark Blue
        "#3a622c", // Dark Green
        "#cd6c35", // Rust
        "#be9c63", // Tan
        "#7FB3D5", // Light Sky Blue
        "#28B463", // Bright Green
        "#2874A6", // Navy Blue
        "#1E8449", // Forest Green
        "#F39C12", // Yellow-Orange
        "#A04000",  // Dark Rust
        "#000000", // Black
        "#FFFFFF", // White
    ];

    // New hierarchical career cluster structure
    const careerClusterHierarchy = {
        "Investing in the Future": {
            color: "#5b9dd0",
            clusters: ["Financial Services"]
        },
        "Connecting & Supporting Success": {
            color: "#53813e",
            clusters: ["Management & Entrepeneurship", "Digital Technology", "Marketing & Sales"]
        },
        "Caring for Communities": {
            color: "#ee7c3e",
            clusters: ["Education", "Healthcare & Human Services", "Public Service & Safety"]
        },
        "Cultivating Resources": {
            color: "#654b1e",
            clusters: ["Agriculture", "Energy & Natural Resources"]
        },
        "Building & Moving": {
            color: "#4C82AC",
            clusters: ["Construction", "Advanced Manufacturing", "Supply Chain & Transportation"]
        },
        "Creating & Experiencing": {
            color: "#3a622c",
            clusters: ["Hospitality, Events, & Tourism", "Arts, Entertainment, & Design"]
        }
    };

    // Define color palettes for child clusters within each parent
    const childColorPalettes = {
        "Investing in the Future": ["#5b9dd0"],
        "Connecting & Supporting Success": ["#53813e", "#7FB3D5", "#28B463"],
        "Caring for Communities": ["#ee7c3e", "#cd6c35", "#F39C12"],
        "Cultivating Resources": ["#654b1e", "#be9c63"],
        "Building & Moving": ["#4C82AC", "#2874A6", "#1E8449"],
        "Creating & Experiencing": ["#3a622c", "#A04000"]
    };

    // Aggregate data for parent categories (inner ring)
    const parentLabels = [];
    const parentData = [];
    const parentColors = [];

    // Aggregate data for child clusters (outer ring)
    const childLabels = [];
    const childData = [];
    const childColors = [];

    Object.entries(careerClusterHierarchy).forEach(([parentName, parentInfo]) => {
        let parentCount = 0;
        const clusters = parentInfo.clusters;
        const palette = childColorPalettes[parentName];

        clusters.forEach((cluster, index) => {
            // Count ELOs where the "Career Cluster" field matches this cluster name
            const count = filteredELOs.filter(elo => elo["Career Cluster"] === cluster).length;

            if (count > 0) {
                childLabels.push(cluster);
                childData.push(count);
                childColors.push(palette[index % palette.length]);
                parentCount += count;
            }
        });

        // Only add parent if it has data
        if (parentCount > 0) {
            parentLabels.push(parentName);
            parentData.push(parentCount);
            parentColors.push(parentInfo.color);
        }
    });


    // Create nested doughnut chart data (inner ring = parents, outer ring = children)
    const pieData = {
        labels: [...parentLabels, ...childLabels], // Combined labels for both rings
        datasets: [
            {
                label: "Parent Categories",
                data: [...parentData, ...Array(childLabels.length).fill(0)], // Parent data + zeros for child positions
                backgroundColor: [...parentColors, ...Array(childLabels.length).fill('transparent')],
                hoverOffset: 4,
                weight: 1, // Inner ring
            },
            {
                label: "Career Clusters",
                data: [...Array(parentLabels.length).fill(0), ...childData], // Zeros for parent positions + child data
                backgroundColor: [...Array(parentLabels.length).fill('transparent'), ...childColors],
                hoverOffset: 4,
                weight: 2, // Outer ring (larger weight = outer position)
            }
        ]
    };

    // bar data
    const barData = {
        labels: ['Interest Exploration', 'Pursuit Partnership', 'Work Based Learning', 'Community Embedded'],
        datasets: [
            {
                data: [
                    filteredELOs.filter(elo => elo["Interest Exploration"] !== "").length,
                    filteredELOs.filter(elo => elo["Pursuit Partnership"] !== "").length,
                    filteredELOs.filter(elo => elo["Work Based Learning"] !== "").length,
                    filteredELOs.filter(elo => elo["Community Embedded"] !== "").length
                ],
                backgroundColor: colors,
                hoverOffset: 4
            }
        ]
    };
    
    // bar options
    const barOptions = {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 1.5,
        plugins: {
            legend: {
                display: false, // Hide the legend
            },
            tooltip: {
                callbacks: {
                    label: function(tooltipItem) {
                        // Get the label from the dataset and the value for the tooltip
                        const datasetLabel = tooltipItem.dataset.label || '';
                        const value = Math.round(tooltipItem.raw); // Round the value to the nearest whole number
                        
                        // Format the tooltip label to include the data name and the count
                        return `${datasetLabel}: ${value}`;
                    }
                },
                titleColor: 'white',
                bodyColor: 'white',
                bodyFont: {
                    size: 15 // Set the font size for the tooltip body
                },
                titleFont: {
                    size: 15 // Set the font size for the tooltip title
                }
            },
            title: {
                display: false, // Hide the title
            }
        },
        scales: {
            x: {
                ticks: {
                    color: '#000000', // Set x-axis labels color to black
                    font: {
                        weight: 'bold', // Make x-axis labels bold
                        size: 14 // Adjust the font size for x-axis labels
                    },
                    stepSize: 1, // Ensure ticks are spaced by whole numbers
                    callback: function(value) {
                        return value; // Show whole numbers directly
                    }
                },
                // Optional: Add a min and max value if needed to limit the axis range
                // min: 0,
                // max: Math.max(...dataCounts)
            },
            y: {
                ticks: {
                    color: '#000000', // Set y-axis labels color to black
                    font: {
                        weight: 'bold', // Make y-axis labels bold
                        size: 14 // Adjust the font size for y-axis labels
                    }
                }
            }
        },
        indexAxis: 'y' // Set to horizontal bar chart
    };

    // pie/doughnut options for nested hierarchical chart
    const pieOptions = {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 1,
        plugins: {
            legend: {
                display: true,
                position: 'left', // Position the legend to the left
                labels: {
                    color: '#000000', // Set the legend text color to black
                    font: {
                        weight: 'bold', // Make legend text bold
                        size: 14 // Set the font size for the legend
                    },
                    boxWidth: 20, // Set the width of the legend box
                    padding: 16, // Add padding around the legend
                    generateLabels: function(chart) {
                        const data = chart.data;
                        if (data.labels.length && data.datasets.length) {
                            // Only show child cluster labels (skip parent category labels)
                            const startIndex = parentLabels.length;
                            return data.labels.slice(startIndex).map((label, i) => {
                                const actualIndex = startIndex + i;
                                const dataset = data.datasets[1]; // Outer ring dataset
                                const backgroundColor = dataset.backgroundColor[actualIndex];
                                return {
                                    text: label,
                                    fillStyle: backgroundColor,
                                    hidden: false,
                                    index: actualIndex,
                                    datasetIndex: 1
                                };
                            });
                        }
                        return [];
                    }
                }
            },
            tooltip: {
                callbacks: {
                    title: function(context) {
                        return context[0].label;
                    },
                    label: function(context) {
                        // Skip transparent/zero values
                        if (context.raw === 0) {
                            return null;
                        }

                        return `${context.raw} ELOs`;
                    }
                },
                // Set tooltip font size
                bodyFont: {
                    size: 15 // Set the font size for the tooltip body
                },
                titleFont: {
                    size: 15 // Set the font size for the tooltip title
                }
            },
        }
    };

    useEffect(() => {
        resetContext();
        fetchPartner(name);
        fetchELOsByPartner(name);
        fetchFilesByPartner(name);
    }, [resetContext, fetchPartner, fetchELOsByPartner, fetchFilesByPartner, name]);

    return (
        <Div
            bg="linear-gradient(135deg, #f5f7fa 0%, #c3cbbb 100%)"
            minH="100vh"
        >
            {loadingPartner ? (
                <Div>
                    <Row
                        justify="center"
                        p={{t:"20rem",b:"22rem"}}
                    >
                        <Icon name="Loading3" size="4rem" color="#654b1e" />
                    </Row>
                </Div>
            ) : (
                <Div style={{overflow: 'hidden', width: '100%'}}>
                    {/* Hero Section */}
                    <Div
                        bg="linear-gradient(135deg, #5b9dd0 0%, #53813e 100%)"
                        p={{t:{xs:"6rem", md:"8rem"}, b:{xs:"3rem", md:"4rem"}, l:{xs:"1rem", md:"2rem"}, r:{xs:"1rem", md:"2rem"}}}
                        style={{
                            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                            width: '100%',
                            boxSizing: 'border-box'
                        }}
                    >
                        <Row justify="center">
                            <Text
                                textColor="white"
                                textWeight="800"
                                textSize={{xs:"heading", sm:"display1", md:"display2"}}
                                m={{b:"0.5rem"}}
                                style={{
                                    textAlign: 'center',
                                    letterSpacing: '-0.5px',
                                    wordBreak: 'break-word'
                                }}
                            >
                                {partner["Name"]}
                            </Text>
                        </Row>
                        {/* Contact Photo */}
                        {!loadingPictures && contactUrl && (
                            <Row justify="center" m={{t:"1rem"}}>
                                <Div style={{position: 'relative', width: '5rem', height: '5rem'}}>
                                    <Image
                                        src={contactUrl}
                                        h="5rem"
                                        w="5rem"
                                        alt="Contact Photo"
                                        rounded="circle"
                                        style={{
                                            objectFit: 'cover',
                                            opacity: contactImageLoaded ? 1 : 0,
                                            transition: 'opacity 0.3s ease-in-out',
                                            border: '3px solid rgba(255,255,255,0.3)'
                                        }}
                                        onLoad={() => setContactImageLoaded(true)}
                                    />
                                    {!contactImageLoaded && (
                                        <Div
                                            h="5rem"
                                            w="5rem"
                                            rounded="circle"
                                            bg="rgba(255,255,255,0.2)"
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Icon name="Loading3" size="1.5rem" color="white" />
                                        </Div>
                                    )}
                                </Div>
                            </Row>
                        )}
                        {/* Partner Since */}
                        {partner["Year Joined"] && (
                            <Row justify="center" m={{t:"0.75rem"}}>
                                <Div
                                    bg="rgba(255,255,255,0.15)"
                                    rounded="xl"
                                    p={{l:"1rem", r:"1rem", t:"0.25rem", b:"0.25rem"}}
                                >
                                    <Text
                                        textColor="white"
                                        textWeight="600"
                                        textSize={{xs:"caption", md:"body"}}
                                        style={{ textAlign: 'center' }}
                                    >
                                        Partner since {partner["Year Joined"]}
                                    </Text>
                                </Div>
                            </Row>
                        )}
                        {partner["Description"] &&
                            <Row justify="center" m={{t:"1.5rem"}}>
                                <Text
                                    textColor="rgba(255,255,255,0.95)"
                                    textWeight="500"
                                    textSize={{xs:"body", md:"subheader"}}
                                    maxW="800px"
                                    style={{
                                        textAlign: 'center',
                                        lineHeight: '1.6',
                                        wordBreak: 'break-word'
                                    }}
                                >
                                    {partner["Description"]}
                                </Text>
                            </Row>
                        }
                    </Div>
                    {/* Image and Contact Section */}
                    <Div bg="#f8f9fa" p={{t:{xs:"2rem", md:"4rem"}, b:{xs:"2rem", md:"4rem"}}} style={{width: '100%', boxSizing: 'border-box'}}>
                        <Div maxW={{xs:"100%", lg:"1400px"}} m="auto" p={{l:{xs:"1rem", md:"2rem"}, r:{xs:"1rem", md:"2rem"}}} style={{width: '100%', boxSizing: 'border-box'}}>
                            <Row flexWrap="wrap" justify="center" style={{width: '100%', gap: '1rem'}}>
                                {/* Image Carousel */}
                                {!loadingPictures && currImage &&
                                    <Col style={{flex: '1 1 400px', maxWidth: '100%', boxSizing: 'border-box'}}>
                                        <Div bg="white" rounded="xl" shadow="4" p={{xs:"1rem", md:"1.5rem"}} style={{width: '100%', boxSizing: 'border-box'}}>
                                            <Div style={{position: 'relative', width: '100%', height: '20rem'}}>
                                                <Image
                                                    key={currImage}
                                                    src={currImage}
                                                    alt="Cover Photo"
                                                    rounded="lg"
                                                    style={{
                                                        objectFit: 'contain',
                                                        width: '100%',
                                                        height: '100%',
                                                        opacity: carouselImageLoaded ? 1 : 0,
                                                        transition: 'opacity 0.3s ease-in-out'
                                                    }}
                                                    onLoad={() => {
                                                        setCarouselImageLoaded(true);
                                                    }}
                                                />
                                                {!carouselImageLoaded && (
                                                    <Div
                                                        h="100%"
                                                        w="100%"
                                                        rounded="lg"
                                                        bg="#e8e8e8"
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <Icon name="Loading3" size="3rem" color="#5b9dd0" />
                                                    </Div>
                                                )}
                                            </Div>
                                            <Row justify="center" align="center" m={{t:"1rem"}} style={{flexWrap: 'nowrap', maxWidth: '100%'}}>
                                                <Button
                                                    h="2rem"
                                                    w="2rem"
                                                    bg="#5b9dd0"
                                                    hoverBg="#4a8bb8"
                                                    rounded="circle"
                                                    m={{r:"0.5rem"}}
                                                    onClick={() => checkIndex > 0 ? setCheckIndex(checkIndex - 1) : null}
                                                    style={{flexShrink: 0}}
                                                >
                                                    <Icon name="LeftArrow" size="16px" color="white" />
                                                </Button>
                                                <Div style={{display: 'flex', alignItems: 'center', overflow: 'hidden', flexShrink: 1, minWidth: 0}}>
                                                    {filteredUrls?.map((url, i) => (
                                                        <Div key={i} onClick={() => setCheckIndex(i)} style={{cursor: 'pointer', flexShrink: 1, minWidth: 0}}>
                                                            <Radiobox
                                                                activeColor="#5b9dd0"
                                                                inactiveColor="#ccc"
                                                                m={{l:".15rem", r:".15rem"}}
                                                                checked={checkIndex === i}
                                                            />
                                                        </Div>
                                                    ))}
                                                </Div>
                                                <Button
                                                    h="2rem"
                                                    w="2rem"
                                                    bg="#5b9dd0"
                                                    hoverBg="#4a8bb8"
                                                    rounded="circle"
                                                    m={{l:"0.5rem"}}
                                                    onClick={() => checkIndex < filteredUrls.length - 1 ? setCheckIndex(checkIndex + 1) : null}
                                                    style={{flexShrink: 0}}
                                                >
                                                    <Icon name="RightArrow" size="16px" color="white" />
                                                </Button>
                                            </Row>
                                        </Div>
                                    </Col>
                                }
                                {/* Contact & Location Card */}
                                {(partner["Contact Name"]?.length > 0 || partner["Contact Title"]?.length > 0 || partner["Company Phone"]?.length > 0 || partner["Mobile Phone"]?.length > 0 || partner["Primary Email"]?.length > 0 || partner["Website"]?.length > 0 || partner["Street Address 1"]?.length > 0) &&
                                    <Col style={{flex: '1 1 400px', maxWidth: '100%', boxSizing: 'border-box'}}>
                                        <Div bg="white" rounded="xl" shadow="4" p={{xs:"1.5rem", md:"2rem"}} style={{width: '100%', boxSizing: 'border-box', height: '100%'}}>
                                            {/* Contact Info */}
                                            {(partner["Contact Name"]?.length > 0 || partner["Contact Title"]?.length > 0 || partner["Company Phone"]?.length > 0 || partner["Primary Email"]?.length > 0) && (
                                                <Div m={{b:"1.5rem"}}>
                                                    <Row align="center" m={{b:"1rem"}}>
                                                        <Icon name="UserSolid" color="#5b9dd0" size="20px" />
                                                        <Text textColor="#2d3748" textWeight="700" textSize={{xs:"body", md:"subheader"}} m={{l:"0.5rem"}}>
                                                            Contact
                                                        </Text>
                                                    </Row>

                                                    {/* Contact Name & Title */}
                                                    {(partner["Contact Name"]?.length > 0 || partner["Contact Title"]?.length > 0) && (
                                                        <Div m={{b:"1rem"}}>
                                                            {partner["Contact Name"]?.length > 0 && (
                                                                <Text textColor="#2d3748" textWeight="600" textSize={{xs:"body", md:"subheader"}}>
                                                                    {partner["Contact Name"]}
                                                                </Text>
                                                            )}
                                                            {partner["Contact Title"]?.length > 0 && (
                                                                <Text textColor="#718096" textSize={{xs:"caption", md:"body"}}>
                                                                    {partner["Contact Title"]}
                                                                </Text>
                                                            )}
                                                        </Div>
                                                    )}

                                                    {/* Contact details in a clean table-like format */}
                                                    {(partner["Company Phone"]?.length > 0 || partner["Mobile Phone"]?.length > 0 || partner["Primary Email"]?.length > 0 || partner["Website"]?.length > 0) && (
                                                        <Div bg="#f8f9fa" rounded="lg" p="1rem">
                                                            {partner["Company Phone"]?.length > 0 && (
                                                                <Row m={{b:"0.5rem"}}>
                                                                    <Text textColor="#718096" textSize={{xs:"caption", md:"body"}} w="5rem">Phone</Text>
                                                                    <Text textColor="#2d3748" textWeight="500" textSize={{xs:"caption", md:"body"}} style={{flex: 1}}>
                                                                        {partner["Company Phone"]}
                                                                    </Text>
                                                                </Row>
                                                            )}
                                                            {partner["Mobile Phone"]?.length > 0 && (
                                                                <Row m={{b:"0.5rem"}}>
                                                                    <Text textColor="#718096" textSize={{xs:"caption", md:"body"}} w="5rem">Mobile</Text>
                                                                    <Text textColor="#2d3748" textWeight="500" textSize={{xs:"caption", md:"body"}} style={{flex: 1}}>
                                                                        {partner["Mobile Phone"]}
                                                                    </Text>
                                                                </Row>
                                                            )}
                                                            {partner["Primary Email"]?.length > 0 && (
                                                                <Row m={{b: partner["Secondary Email"]?.length > 0 || partner["Website"]?.length > 0 ? "0.5rem" : "0"}}>
                                                                    <Text textColor="#718096" textSize={{xs:"caption", md:"body"}} w="5rem">Email</Text>
                                                                    <Text textColor="#5b9dd0" textWeight="500" textSize={{xs:"caption", md:"body"}} style={{flex: 1, wordBreak: 'break-word'}}>
                                                                        {partner["Primary Email"]}
                                                                    </Text>
                                                                </Row>
                                                            )}
                                                            {partner["Secondary Email"]?.length > 0 && (
                                                                <Row m={{b: partner["Website"]?.length > 0 ? "0.5rem" : "0"}}>
                                                                    <Text textColor="#718096" textSize={{xs:"caption", md:"body"}} w="5rem"></Text>
                                                                    <Text textColor="#5b9dd0" textWeight="500" textSize={{xs:"caption", md:"body"}} style={{flex: 1, wordBreak: 'break-word'}}>
                                                                        {partner["Secondary Email"]}
                                                                    </Text>
                                                                </Row>
                                                            )}
                                                            {partner["Website"]?.length > 0 && (
                                                                <Row m={{b:"0"}}>
                                                                    <Text textColor="#718096" textSize={{xs:"caption", md:"body"}} w="5rem">Website</Text>
                                                                    <a href={getHref} target="_blank" rel="noopener noreferrer" style={{textDecoration: 'none', flex: 1}}>
                                                                        <Text textColor="#53813e" textWeight="500" textSize={{xs:"caption", md:"body"}} style={{wordBreak: 'break-word'}}>
                                                                            {partner["Website"]}
                                                                        </Text>
                                                                    </a>
                                                                </Row>
                                                            )}
                                                        </Div>
                                                    )}
                                                </Div>
                                            )}
                                            {/* Location Info */}
                                            {(partner["Street Address 1"]?.length > 0 || (partner["City"]?.length > 0 && partner["State"]?.length > 0)) && (
                                                <Div>
                                                    <Row align="center" m={{b:"1rem"}}>
                                                        <Icon name="LocationSolid" color="#53813e" size="20px" />
                                                        <Text textColor="#2d3748" textWeight="700" textSize={{xs:"body", md:"subheader"}} m={{l:"0.5rem"}}>
                                                            Location
                                                        </Text>
                                                    </Row>

                                                    {/* Location details in a clean table-like format */}
                                                    <Div bg="#f8f9fa" rounded="lg" p="1rem">
                                                        {partner["Street Address 1"]?.length > 0 && (
                                                            <Row m={{b:"0.5rem"}}>
                                                                <Text textColor="#718096" textSize={{xs:"caption", md:"body"}} w="5rem">Address</Text>
                                                                <Text textColor="#2d3748" textWeight="500" textSize={{xs:"caption", md:"body"}} style={{flex: 1}}>
                                                                    {partner["Street Address 1"]}
                                                                    {partner["Street Address 2"]?.length > 0 && `, ${partner["Street Address 2"]}`}
                                                                </Text>
                                                            </Row>
                                                        )}
                                                        {(partner["City"]?.length > 0 && partner["State"]?.length > 0) && (
                                                            <Row m={{b: partner["County"]?.length > 0 ? "0.5rem" : "0"}}>
                                                                <Text textColor="#718096" textSize={{xs:"caption", md:"body"}} w="5rem">City</Text>
                                                                <Text textColor="#2d3748" textWeight="500" textSize={{xs:"caption", md:"body"}} style={{flex: 1}}>
                                                                    {partner["City"]}, {partner["State"]} {partner["ZIP"] || ''}
                                                                </Text>
                                                            </Row>
                                                        )}
                                                        {partner["County"]?.length > 0 && (
                                                            <Row m={{b:"0"}}>
                                                                <Text textColor="#718096" textSize={{xs:"caption", md:"body"}} w="5rem">County</Text>
                                                                <Text textColor="#2d3748" textWeight="500" textSize={{xs:"caption", md:"body"}} style={{flex: 1}}>
                                                                    {partner["County"]}
                                                                </Text>
                                                            </Row>
                                                        )}
                                                    </Div>

                                                    {/* Map */}
                                                    {partner["Street Address 1"]?.length > 0 && partner["City"]?.length > 0 && partner["State"]?.length > 0 && (
                                                        <Div m={{t:"1rem"}} rounded="lg" style={{overflow: 'hidden'}}>
                                                            {!loadingPartner ? (
                                                                <SimpleMap address={partner["Street Address 1"] + ", " + partner["City"] + ", " + partner["State"]} />
                                                            ) : (
                                                                <Div h="10rem" bg="#e8e8e8" d="flex" align="center" justify="center">
                                                                    <Icon name="Loading3" size="2rem" color="#5b9dd0" />
                                                                </Div>
                                                            )}
                                                        </Div>
                                                    )}
                                                </Div>
                                            )}
                                        </Div>
                                    </Col>
                                }
                            </Row>
                        </Div>
                    </Div>
                    {/* Partnership Details Section */}
                    {((partner["Norris Mukwonago"] === "TRUE" || partner["Learn Milwaukee"] === "TRUE" || partner["Virtual"] === "TRUE") ||
                      (partner["Home Base"] === "TRUE" || partner["Learning Hub"] === "TRUE" || partner["Field Site"] === "TRUE") ||
                      (interestExploration.length > 0 || pursuitPartnership.length > 0 || wbl.length > 0 || community.length > 0) ||
                      (getCareerClusters(partner, careerClusterFields).length > 0)) &&
                        <Div bg="white" p={{t:{xs:"2rem", md:"4rem"}, b:{xs:"2rem", md:"4rem"}}} style={{width: '100%', boxSizing: 'border-box'}}>
                            <Div maxW={{xs:"100%", lg:"1400px"}} m="auto" p={{l:{xs:"1rem", md:"2rem"}, r:{xs:"1rem", md:"2rem"}}} style={{width: '100%', boxSizing: 'border-box'}}>
                                <Text
                                    textColor="#2d3748"
                                    textWeight="700"
                                    textSize={{xs:"subheader", sm:"heading", md:"display1"}}
                                    textAlign="center"
                                    p={{b:{xs:"1.5rem", md:"2rem"}}}
                                >
                                    Partnership Details
                                </Text>

                                <Row flexWrap="wrap" justify="center" style={{width: '100%', gap: '1rem'}}>
                                    {/* ELO Location Card */}
                                    {(partner["Norris Mukwonago"] === "TRUE" || partner["Learn Milwaukee"] === "TRUE" || partner["Virtual"] === "TRUE") &&
                                        <Col style={{flex: '1 1 auto', minWidth: '200px', maxWidth: '100%', boxSizing: 'border-box'}}>
                                            <Div bg="#f8f9fa" rounded="xl" shadow="3" p={{xs:"1rem", md:"1.5rem"}} style={{height: '100%', boxSizing: 'border-box'}}>
                                                <Row align="center" m={{b:"0.75rem"}}>
                                                    <Icon name="Location" color="#5b9dd0" size="20px" />
                                                    <Text textColor="#2d3748" textWeight="700" textSize={{xs:"body", md:"subheader"}} m={{l:"0.5rem"}}>
                                                        ELO Location
                                                    </Text>
                                                </Row>
                                                {partner["Norris Mukwonago"] === "TRUE" && (
                                                    <Text textColor="#4a5568" textSize={{xs:"caption", md:"body"}} m={{b:"0.25rem"}}>• Norris, Mukwonago</Text>
                                                )}
                                                {partner["Learn Milwaukee"] === "TRUE" && (
                                                    <Text textColor="#4a5568" textSize={{xs:"caption", md:"body"}} m={{b:"0.25rem"}}>• Learn Milwaukee</Text>
                                                )}
                                                {partner["Virtual"] === "TRUE" && (
                                                    <Text textColor="#4a5568" textSize={{xs:"caption", md:"body"}}>• Virtual</Text>
                                                )}
                                            </Div>
                                        </Col>
                                    }

                                    {/* Learning Hub Card */}
                                    {(partner["Home Base"] === "TRUE" || partner["Learning Hub"] === "TRUE" || partner["Field Site"] === "TRUE") &&
                                        <Col style={{flex: '1 1 auto', minWidth: '200px', maxWidth: '100%', boxSizing: 'border-box'}}>
                                            <Div bg="#f8f9fa" rounded="xl" shadow="3" p={{xs:"1rem", md:"1.5rem"}} style={{height: '100%', boxSizing: 'border-box'}}>
                                                <Row align="center" m={{b:"0.75rem"}}>
                                                    <Icon name="Home" color="#53813e" size="20px" />
                                                    <Text textColor="#2d3748" textWeight="700" textSize={{xs:"body", md:"subheader"}} m={{l:"0.5rem"}}>
                                                        Learning Hub
                                                    </Text>
                                                </Row>
                                                {partner["Home Base"] === "TRUE" && (
                                                    <Text textColor="#4a5568" textSize={{xs:"caption", md:"body"}} m={{b:"0.25rem"}}>• Home Base</Text>
                                                )}
                                                {partner["Learning Hub"] === "TRUE" && (
                                                    <Text textColor="#4a5568" textSize={{xs:"caption", md:"body"}} m={{b:"0.25rem"}}>• Learning Hub</Text>
                                                )}
                                                {partner["Field Site"] === "TRUE" && (
                                                    <Text textColor="#4a5568" textSize={{xs:"caption", md:"body"}}>• Field Site</Text>
                                                )}
                                            </Div>
                                        </Col>
                                    }

                                    {/* ELO Types Card */}
                                    {(interestExploration.length > 0 || pursuitPartnership.length > 0 || wbl.length > 0 || community.length > 0) &&
                                        <Col style={{flex: '1 1 auto', minWidth: '280px', maxWidth: '100%', boxSizing: 'border-box'}}>
                                            <Div bg="#f8f9fa" rounded="xl" shadow="3" p={{xs:"1rem", md:"1.5rem"}} style={{height: '100%', boxSizing: 'border-box'}}>
                                                <Text textColor="#2d3748" textWeight="700" textSize={{xs:"body", md:"subheader"}} m={{b:"1rem"}} textAlign="center">
                                                    ELO Types
                                                </Text>
                                                <Row flexWrap="wrap" justify="center">
                                                    {interestExploration.length > 0 && (
                                                        <Col size={{xs:"6", sm:"3"}} p="0.5rem">
                                                            <Div textAlign="center">
                                                                <Div bg="#5b9dd0" rounded="circle" h="2.5rem" w="2.5rem" d="flex" align="center" justify="center" m={{l:"auto", r:"auto", b:"0.5rem"}}>
                                                                    <Text textColor="white" textWeight="700" textSize="caption">IE</Text>
                                                                </Div>
                                                                {interestExploration.map((interest, idx) => (
                                                                    <Text key={idx} textColor="#4a5568" textSize="caption" m={{b:"0.25rem"}}>{interest}</Text>
                                                                ))}
                                                            </Div>
                                                        </Col>
                                                    )}
                                                    {pursuitPartnership.length > 0 && (
                                                        <Col size={{xs:"6", sm:"3"}} p="0.5rem">
                                                            <Div textAlign="center">
                                                                <Div bg="#53813e" rounded="circle" h="2.5rem" w="2.5rem" d="flex" align="center" justify="center" m={{l:"auto", r:"auto", b:"0.5rem"}}>
                                                                    <Text textColor="white" textWeight="700" textSize="caption">PP</Text>
                                                                </Div>
                                                                {pursuitPartnership.map((interest, idx) => (
                                                                    <Text key={idx} textColor="#4a5568" textSize="caption" m={{b:"0.25rem"}}>{interest}</Text>
                                                                ))}
                                                            </Div>
                                                        </Col>
                                                    )}
                                                    {wbl.length > 0 && (
                                                        <Col size={{xs:"6", sm:"3"}} p="0.5rem">
                                                            <Div textAlign="center">
                                                                <Div bg="#ee7c3e" rounded="circle" h="2.5rem" w="2.5rem" d="flex" align="center" justify="center" m={{l:"auto", r:"auto", b:"0.5rem"}}>
                                                                    <Text textColor="white" textWeight="700" textSize="caption">WBL</Text>
                                                                </Div>
                                                                {wbl.map((interest, idx) => (
                                                                    <Text key={idx} textColor="#4a5568" textSize="caption" m={{b:"0.25rem"}}>{interest}</Text>
                                                                ))}
                                                            </Div>
                                                        </Col>
                                                    )}
                                                    {community.length > 0 && (
                                                        <Col size={{xs:"6", sm:"3"}} p="0.5rem">
                                                            <Div textAlign="center">
                                                                <Div bg="#654b1e" rounded="circle" h="2.5rem" w="2.5rem" d="flex" align="center" justify="center" m={{l:"auto", r:"auto", b:"0.5rem"}}>
                                                                    <Text textColor="white" textWeight="700" textSize="caption">CE</Text>
                                                                </Div>
                                                                {community.map((interest, idx) => (
                                                                    <Text key={idx} textColor="#4a5568" textSize="caption" m={{b:"0.25rem"}}>{interest}</Text>
                                                                ))}
                                                            </Div>
                                                        </Col>
                                                    )}
                                                </Row>
                                            </Div>
                                        </Col>
                                    }
                                </Row>

                                {/* Career Clusters */}
                                {getCareerClusters(partner, careerClusterFields).length > 0 && (
                                    <Div m={{t:"1.5rem"}}>
                                        <Div bg="#f8f9fa" rounded="xl" shadow="3" p={{xs:"1rem", md:"2rem"}} style={{width: '100%', boxSizing: 'border-box'}}>
                                            <Text textColor="#2d3748" textWeight="700" textSize={{xs:"body", md:"subheader"}} m={{b:"1rem"}} textAlign="center">
                                                Career Clusters
                                            </Text>
                                            {getCareerClusters(partner, careerClusterFields).map((key, idx) => (
                                                <Div key={idx} m={{b: idx < getCareerClusters(partner, careerClusterFields).length - 1 ? "1rem" : "0"}} p={{b: idx < getCareerClusters(partner, careerClusterFields).length - 1 ? "1rem" : "0"}} style={{borderBottom: idx < getCareerClusters(partner, careerClusterFields).length - 1 ? '1px solid #e2e8f0' : 'none'}}>
                                                    <Text textColor="#5b9dd0" textWeight="600" textSize={{xs:"body", md:"subheader"}} m={{b:"0.5rem"}}>
                                                        {key}
                                                    </Text>
                                                    <Row flexWrap="wrap">
                                                        {getCareerArray(key).map((key2, idx2) => (
                                                            <Col key={idx2} size={{xs:"12", sm:"6", md:"4"}} p={{xs:"0.25rem", md:"0.5rem"}}>
                                                                <Text textColor="#4a5568" textSize={{xs:"caption", md:"body"}}>• {key2}</Text>
                                                            </Col>
                                                        ))}
                                                    </Row>
                                                </Div>
                                            ))}
                                        </Div>
                                    </Div>
                                )}
                            </Div>
                        </Div>
                    }
                    {/* Engagement Stats Section */}
                    {!loadingELO &&
                        <Div bg="#f8f9fa" p={{t:{xs:"2rem", md:"4rem"}, b:{xs:"2rem", md:"4rem"}}} style={{overflow: 'hidden', width: '100%'}}>
                            <Div maxW={{xs:"100%", lg:"1400px"}} m="auto" p={{l:{xs:"1rem", md:"2rem"}, r:{xs:"1rem", md:"2rem"}}} style={{width: '100%', boxSizing: 'border-box'}}>
                                <Text
                                    textColor="#2d3748"
                                    textWeight="700"
                                    textSize={{xs:"subheader", sm:"heading", md:"display1"}}
                                    textAlign="center"
                                    p={{b:{xs:"1.5rem", md:"2rem"}}}
                                >
                                    Partner Engagement
                                </Text>
                                <Row justify="center" p={{b:{xs:"2rem", md:"3rem"}}} flexWrap="wrap" style={{maxWidth: '100%'}}>
                                    <Dropdown
                                        isOpen={showTermDropdown}
                                        onClick={() => setShowTermDropdown(!showTermDropdown)}
                                        menu={termList}
                                        w={{xs:"calc(50% - 0.5rem)", sm:"45%", md:"30%", lg:"20%"}}
                                        m={{xs:".25rem", md:".5rem"}}
                                        textWeight="600"
                                        borderColor="#5b9dd0"
                                        focusBorderColor="#5b9dd0"
                                        bg="white"
                                        border="2px solid"
                                        textColor="#2d3748"
                                        rounded="lg"
                                        shadow="2"
                                        textSize={{xs:"caption", md:"body"}}
                                    >
                                        {selectedTerm}
                                    </Dropdown>
                                    <Dropdown
                                        isOpen={showYearDropdown}
                                        onClick={() => setShowYearDropdown(!showYearDropdown)}
                                        menu={sortList}
                                        w={{xs:"calc(50% - 0.5rem)", sm:"45%", md:"30%", lg:"20%"}}
                                        m={{xs:".25rem", md:".5rem"}}
                                        textWeight="600"
                                        borderColor="#5b9dd0"
                                        focusBorderColor="#5b9dd0"
                                        bg="white"
                                        border="2px solid"
                                        textColor="#2d3748"
                                        rounded="lg"
                                        shadow="2"
                                        textSize={{xs:"caption", md:"body"}}
                                    >
                                        {selectedYear}
                                    </Dropdown>
                                </Row>
                                <Row justify="center" p={{b:{xs:"2rem", md:"3rem"}}} flexWrap="wrap" style={{maxWidth: '100%', gap: '1rem'}}>
                                    <Col size={{xs:"12", md:"5"}} style={{maxWidth: '100%', boxSizing: 'border-box'}}>
                                        <Div
                                            bg="linear-gradient(135deg, #5b9dd0 0%, #4a8bb8 100%)"
                                            rounded="xl"
                                            p={{xs:"1rem", sm:"1.5rem", md:"2rem"}}
                                            textAlign="center"
                                            shadow="4"
                                            style={{width: '100%', boxSizing: 'border-box', minWidth: 0}}
                                        >
                                            <Text
                                                textColor="white"
                                                textSize={{xs:"title", sm:"heading", md:"display1", lg:"display2"}}
                                                textWeight="800"
                                                p={{b:".5rem"}}
                                                style={{wordBreak: 'break-word', overflow: 'hidden'}}
                                            >
                                                {sumLearnersServed(filteredELOs)}
                                            </Text>
                                            <Text
                                                textColor="white"
                                                textSize={{xs:"caption", sm:"body", md:"subheader", lg:"title"}}
                                                textWeight="600"
                                                style={{opacity: 0.95, wordBreak: 'break-word'}}
                                            >
                                                Learners Served
                                            </Text>
                                        </Div>
                                    </Col>
                                    <Col size={{xs:"12", md:"5"}} style={{maxWidth: '100%', boxSizing: 'border-box'}}>
                                        <Div
                                            bg="linear-gradient(135deg, #53813e 0%, #3f6630 100%)"
                                            rounded="xl"
                                            p={{xs:"1rem", sm:"1.5rem", md:"2rem"}}
                                            textAlign="center"
                                            shadow="4"
                                            style={{width: '100%', boxSizing: 'border-box', minWidth: 0}}
                                        >
                                            <Text
                                                textColor="white"
                                                textSize={{xs:"title", sm:"heading", md:"display1", lg:"display2"}}
                                                textWeight="800"
                                                p={{b:".5rem"}}
                                                style={{wordBreak: 'break-word', overflow: 'hidden'}}
                                            >
                                                {filteredELOs?.length}
                                            </Text>
                                            <Text
                                                textColor="white"
                                                textSize={{xs:"caption", sm:"body", md:"subheader", lg:"title"}}
                                                textWeight="600"
                                                style={{opacity: 0.95, wordBreak: 'break-word'}}
                                            >
                                                ELOs Completed
                                            </Text>
                                        </Div>
                                    </Col>
                                </Row>
                                {filteredELOs.length > 0 && (
                                    <>
                                        <Row justify="center" p={{t:"2rem", b:"2rem"}}>
                                            <Col size="12">
                                                <Div bg="white" rounded="xl" shadow="4" p={{xs:"1rem", md:"2rem"}} style={{overflow: 'hidden', width: '100%', boxSizing: 'border-box'}}>
                                                    <Text textColor="#2d3748" textSize={{xs:"subheader", md:"title"}} textWeight="700" textAlign="center" p={{b:"1.5rem"}}>
                                                        Career Clusters Distribution
                                                    </Text>
                                                    <Div style={{maxWidth: '100%', width: '100%', margin: '0 auto', position: 'relative', height: 'auto'}}>
                                                        <Doughnut data={pieData} options={pieOptions} />
                                                    </Div>
                                                    <Row justify="center" p={{t:"1.5rem"}}>
                                                        <a
                                                            href="https://docs.google.com/document/d/1Yk9eiakHZpQgDXTGuXnKpJ2lVstjYXwfyfnSnbV8Jzs/edit?usp=sharing"
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            style={{textDecoration: 'none'}}
                                                        >
                                                            <Button
                                                                bg="#5b9dd0"
                                                                hoverBg="#4a8bb8"
                                                                textColor="white"
                                                                rounded="lg"
                                                                p={{l:"1.5rem", r:"1.5rem"}}
                                                                shadow="2"
                                                            >
                                                                View Career Cluster Descriptions
                                                            </Button>
                                                        </a>
                                                    </Row>
                                                </Div>
                                            </Col>
                                        </Row>

                                        <Row justify="center" p={{t:"1rem", b:"3rem"}}>
                                            <Col size="12">
                                                <Div bg="white" rounded="xl" shadow="4" p={{xs:"1rem", md:"2rem"}} style={{overflow: 'hidden', width: '100%', boxSizing: 'border-box'}}>
                                                    <Text textColor="#2d3748" textSize={{xs:"subheader", md:"title"}} textWeight="700" textAlign="center" p={{b:"1.5rem"}}>
                                                        ELO Types Breakdown
                                                    </Text>
                                                    <Div style={{position: 'relative', height: 'auto', width: '100%', maxWidth: '100%'}}>
                                                        <Bar data={barData} options={barOptions} />
                                                    </Div>
                                                </Div>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                            </Div>
                        </Div>
                    }
                </Div>
            )}
        </Div>
    );
};

export default PartnerPage;